import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { REACT_APP_API_URL } from 'src/constants/dotenv';

import './chaosEffectsWidget.scss';

const API_PREFIX = String(REACT_APP_API_URL || '').replace(/\/$/, '') || '/api';
const MAX_SEEN_EFFECTS = 100;
const EFFECTS = { flash: 'flash', flashbang: 'flash', center_crop: 'center_crop' };

const resolveUuidFromPath = (pathname, routeUuid) => {
  if (routeUuid) return routeUuid;
  const parts = String(pathname || '').split('/').filter(Boolean);
  return parts[1] === 'chaos-effects' ? parts[0] : '';
};

const rememberEffect = (ids, id) => {
  if (!id || ids.has(id)) return false;
  ids.add(id);
  if (ids.size > MAX_SEEN_EFFECTS) ids.delete(ids.values().next().value);
  return true;
};

export const ChaosEffectsWidgetView = () => {
  const { uuid: routeUuid } = useParams();
  const { pathname } = useLocation();
  const uuid = useMemo(() => resolveUuidFromPath(pathname, routeUuid), [pathname, routeUuid]);
  const seenIdsRef = useRef(new Set());
  const queueRef = useRef([]);
  const runningRef = useRef(false);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const [effect, setEffect] = useState(null);

  const runNext = useCallback(() => {
    if (runningRef.current) return;
    let next = queueRef.current.shift();
    while (next && Date.now() >= next.startedAt + next.durationMs) next = queueRef.current.shift();
    if (!next) return;

    runningRef.current = true;
    const elapsedMs = Math.max(0, Date.now() - next.startedAt);
    const remainingMs = next.durationMs - elapsedMs;
    if (remainingMs <= 0) { runningRef.current = false; runNext(); return; }

    setEffect({ ...next, elapsedMs });
    timerRef.current = setTimeout(() => {
      runningRef.current = false;
      if (mountedRef.current) setEffect(null);
      runNext();
    }, remainingMs);
  }, []);

  useEffect(() => {
    if (!uuid) return undefined;
    const source = new EventSource(`${API_PREFIX}/chaos-effects-widget/${encodeURIComponent(uuid)}/stream`);
    source.addEventListener('chaos_visual_effect', (raw) => {
      try {
        const data = JSON.parse(raw.data || '{}');
        const effectId = String(data.effectId || '');
        const mode = EFFECTS[String(data.mode || '').toLowerCase()];
        const durationMs = Math.max(0, Number(data.durationMs || 0));
        const startedAt = Number(data.startedAt || 0);
        if (!mode || !effectId || !durationMs || !startedAt || !rememberEffect(seenIdsRef.current, effectId)) return;
        queueRef.current.push({ effectId, mode, durationMs, startedAt });
        runNext();
      } catch (_error) { /* ignore malformed SSE data */ }
    });
    return () => source.close();
  }, [runNext, uuid]);

  useEffect(() => {
    document.documentElement.classList.add('chaos-effects-widget-page');
    document.body.classList.add('chaos-effects-widget-page');
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      document.documentElement.classList.remove('chaos-effects-widget-page');
      document.body.classList.remove('chaos-effects-widget-page');
    };
  }, []);

  const style = effect ? { animationDuration: `${effect.durationMs}ms`, animationDelay: `-${effect.elapsedMs}ms` } : {};
  return <div className="chaos-effects-widget" aria-hidden="true">
    {effect?.mode === 'flash' ? <div key={effect.effectId} className="chaos-effects-widget__flash" style={style} /> : null}
    {effect?.mode === 'center_crop' ? <div key={effect.effectId} className="chaos-effects-widget__crop" style={style}><i /><i /><i /><i /></div> : null}
  </div>;
};
